import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Globe, Star, Clock, Search, ChefHat, AlertCircle } from 'lucide-react';
import { PageSeo } from '@/components/seo/PageSeo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RestaurantsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [restaurants, setRestaurants] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_URL}/api/restaurants/locations/countries`);
        const data = await res.json();
        setCountries(data.countries || []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Fetch cities when country changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedCountry) {
        setCities([]);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/restaurants/locations/cities?country=${encodeURIComponent(selectedCountry)}`);
        const data = await res.json();
        setCities(data.cities || []);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCities();
  }, [selectedCountry]);

  // Fetch restaurants
  useEffect(() => {
    const fetchRestaurants = async () => {
      setLoading(true);
      try {
        let url = `${API_URL}/api/restaurants?limit=50`;
        if (selectedCountry) url += `&country=${encodeURIComponent(selectedCountry)}`;
        if (selectedCity) url += `&city=${encodeURIComponent(selectedCity)}`;
        if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
        
        const res = await fetch(url);
        const data = await res.json();
        setRestaurants(data.restaurants || []);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('Failed to fetch restaurants:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurants();
  }, [selectedCountry, selectedCity, searchQuery]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCountry) params.set('country', selectedCountry);
    if (selectedCity) params.set('city', selectedCity);
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params);
  }, [selectedCountry, selectedCity, searchQuery, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Search is already reactive
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return '🍽️';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white">
        <PageSeo
          title={t('restaurantsPage.title', 'Türk Restoranları')}
          description={t('restaurantsPage.subtitle', 'Dünyanın dört bir yanındaki Türk restoranlarını keşfedin')}
          segments={['restaurants']}
        />
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-12 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="flex items-center gap-3 mb-4">
              <ChefHat className="w-10 h-10" />
              <h1 className="text-3xl md:text-4xl font-bold">{t('restaurantsPage.title', 'Türk Restoranları')}</h1>
            </div>
            <p className="text-red-100 text-lg mb-6">
              {t('restaurantsPage.subtitle', 'Dünyanın dört bir yanındaki en iyi Türk restoranlarını keşfedin')}
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t('restaurantsPage.searchPlaceholder', 'Restoran ara...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white text-gray-900 h-12"
                />
              </div>
            </form>
          </div>
        </div>

        <div className="container mx-auto max-w-6xl px-4 py-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-8">
            <Select value={selectedCountry || 'all'} onValueChange={(v) => { setSelectedCountry(v === 'all' ? '' : v); setSelectedCity(''); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('restaurantsPage.selectCountry', 'Ülke seçin')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('restaurantsPage.allCountries', 'Tüm Ülkeler')}</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    {getCountryFlag(c.code)} {c.name} ({c.restaurant_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedCity || 'all'} onValueChange={(v) => setSelectedCity(v === 'all' ? '' : v)} disabled={!selectedCountry}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t('restaurantsPage.selectCity', 'Şehir seçin')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('restaurantsPage.allCities', 'Tüm Şehirler')}</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    {c.name} ({c.restaurant_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(selectedCountry || selectedCity || searchQuery) && (
              <Button 
                variant="outline" 
                onClick={() => { setSelectedCountry(''); setSelectedCity(''); setSearchQuery(''); }}
              >
                {t('restaurantsPage.clearFilters', 'Filtreleri Temizle')}
              </Button>
            )}
          </div>

          {/* Results Count */}
          <p className="text-gray-600 mb-6">{total} {t('restaurantsPage.restaurantsFound', 'restoran bulundu')}</p>

          {/* Restaurant Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-40 bg-gray-200 rounded-lg mb-4" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : restaurants.length === 0 ? (
            <div className="text-center py-12">
              <ChefHat className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">{t('restaurantsPage.noRestaurants', 'Restoran bulunamadı')}</h3>
              <p className="text-gray-500">{t('restaurantsPage.tryDifferentFilter', 'Farklı bir arama veya filtre deneyin')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restaurants.map((restaurant) => (
                <Link 
                  key={restaurant.id} 
                  to={`/restaurants/${restaurant.id}`}
                  className="block"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow overflow-hidden group">
                    <div className="relative h-48 bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
                      {restaurant.images?.[0] ? (
                        <img 
                          src={restaurant.images[0]} 
                          alt={restaurant.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ChefHat className="w-20 h-20 text-red-300" />
                      )}
                      
                      {/* Country Flag */}
                      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-sm">
                        {getCountryFlag(restaurant.country_code)} {restaurant.country}
                      </div>
                      
                      {/* Order Status Badge */}
                      {!restaurant.can_order && (
                        <div className="absolute top-3 right-3">
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t('restaurantsPage.comingSoon', 'Yakında')}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-lg mb-1 group-hover:text-red-600 transition-colors">
                        {restaurant.name}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                        <MapPin className="w-4 h-4" />
                        <span>{restaurant.city}, {restaurant.country}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span>{restaurant.rating?.toFixed(1) || '4.5'}</span>
                        </div>
                        <span className="text-gray-400">{restaurant.price_range || '€€'}</span>
                        {restaurant.features?.includes('Halal') && (
                          <Badge variant="outline" className="text-green-600 border-green-300">{t('restaurantsPage.halal', 'Helal')}</Badge>
                        )}
                      </div>
                      
                      {!restaurant.can_order && (
                        <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {t('restaurantsPage.notYetJoined', 'Bu restoran henüz ailemize katılmamış')}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
  );
};

export default RestaurantsPage;
